import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from 'react';

import { GlobalStyles } from '@mui/material';
import * as Sentry from '@sentry/react';

import {
  DsVarOverrides,
  overrideDsVars,
  SxProps,
  Page as DSPage,
  PageProps,
  mergeSx,
  Stack,
} from '@cast/design-system';
import { makeLogger } from '@cast/utils';

import { RootErrorBoundary } from './RootErrorBoundary';
import { PageAnalyticsPropsUnion, usePageAnalytics } from './usePageAnalytics';

import type { StackProps } from '@cast/design-system';

const PageContext = createContext({ isInPage: false });

const log = makeLogger('Page');

type Props = PropsWithChildren<
  | {
      analytics: PageAnalyticsPropsUnion;
      emitPageVisitEvent?: boolean;
      testId?: string;
      sx?: SxProps;
      dsVarOverrides?: DsVarOverrides;
    } & (
      | { useDsPage: true; rootProps?: Partial<PageProps> }
      | { useDsPage?: false | undefined; rootProps?: StackProps }
    )
>;

export const Page = ({
  children,
  analytics,
  rootProps,
  testId = 'page-root',
  sx,
  dsVarOverrides,
  useDsPage,
}: Props) => {
  const { isInPage } = useContext(PageContext);

  const rootRef = useRef<HTMLDivElement>(null);
  const nestedPageErrorReported = useRef<boolean>(undefined);

  const analyticsId = (() => {
    if (typeof analytics === 'string') {
      return analytics;
    }
    if (typeof analytics === 'object') {
      return analytics.title;
    }
    return 'NO_ANALYTICS';
  })();

  useEffect(() => {
    log(`Mounted: ${analyticsId}`);
    return () => log(`Unmounted: ${analyticsId}`);
  }, [analyticsId]);

  const varOverrides = useMemo(() => {
    if (!dsVarOverrides) {
      return;
    }
    return overrideDsVars(dsVarOverrides);
  }, [dsVarOverrides]);

  const normalizedAnalyticsArgs = usePageAnalytics({
    rootRef,
    analytics,
    enabled: !!analytics,
  });

  useEffect(() => {
    if (isInPage && !nestedPageErrorReported.current) {
      const message = `Page "${
        normalizedAnalyticsArgs?.title || 'NO_NAME'
      }" is rendered inside another page.`;
      Sentry.captureMessage(message);
      nestedPageErrorReported.current = true;
    }
  }, [isInPage, nestedPageErrorReported, normalizedAnalyticsArgs?.title]);

  return (
    <RootErrorBoundary>
      <PageContext.Provider value={{ isInPage: true }}>
        {!!varOverrides && <GlobalStyles styles={{ ':root': varOverrides }} />}
        {useDsPage ? (
          <DSPage testId={testId} ref={rootRef} sx={sx} {...rootProps}>
            {children}
          </DSPage>
        ) : (
          <Stack
            data-testid={testId}
            height="100%"
            ref={rootRef}
            {...rootProps}
            sx={mergeSx(sx, rootProps?.sx)}
          >
            {children}
          </Stack>
        )}
      </PageContext.Provider>
    </RootErrorBoundary>
  );
};
